/**
 * @fileoverview Accessibility utilities for the WebChat application.
 *
 * This module provides functions related to motion preferences, scrolling, and keyboard navigation
 * to enhance the accessibility of the WebChat interface. It includes utilities for detecting user
 * motion preferences, cross-browser scrolling implementations, and keyboard shortcut handling.
 *
 * @author Palash
 * @version 1.0.0
 */

//#region Imports Packages and Libs

import { ScrollOptions } from "../../types/a11y_types";
import { StringUtils } from "./../string_utils";

//#endregion

//#region Motion Preference Detection

/**
 * Check if the user prefers reduced motion
 * @returns {boolean} True if the user prefers reduced motion, false otherwise
 */
export function prefersReducedMotion(): boolean {
	try {
		if (typeof window === "undefined") return false;
		return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	} catch (error) {
		//console.error("Error checking reduced motion preference:", error);
		return false;
	}
}

//#endregion

//#region Scrolling Functions

/**
 * Specialized method for scrolling within the chat with browser-specific optimizations
 * @param options Object containing all scroll parameters
 * @param direction "top" to scroll to the top, "bottom" to scroll to the bottom
 */
function scrollWithPreference(options: ScrollOptions, direction: "top" | "bottom"): void {
	try {
		// Destructure options for easier access
		let { element, isIFrameLoaded, isTilesOpened, chatBodyElement } = options;
		const isTop = direction === "top"; // Calculate appropriate offset based on direction

		// Skip, if no element is provided
		if (!element || !chatBodyElement) return;

		// Get the offsetTop value for the last message
		const $lastMessage = jQuery(".satisfi_messages section:not(.satisfi_chat-modal-body):last");
		const lastMessageElem = $lastMessage && $lastMessage.length > 0 ? $lastMessage[0] : null;
		const offsetTop = lastMessageElem ? lastMessageElem.offsetTop : 0;

		// Check if running on Mac/iOS devices which need special handling
		if (isMacLike()) {
			// Mac/iOS devices need custom scrolling
			if (chatBodyElement) {
				// For top: use offsetTop, for bottom: use maximum scroll height
				chatBodyElement.scrollTop = isTop ? 0 : chatBodyElement.scrollHeight;
			} else {
				// Fallback to standard scrollIntoView (true for top, false for bottom)
				element.scrollIntoView(isTop);
			}
		} else {
			// Check if this page is inside an iframe and also a Tile
			if (isIFrameLoaded && isTilesOpened) {
				// Fix for scrollIntoView() causing the whole page to move in Tiles
				chatBodyElement.scroll({
					top: isTop ? 0 : offsetTop,
					behavior: isTop ? "auto" : "smooth",
				});
			} else if (isTop) {
				chatBodyElement.scrollTop = 0;
			} else if (lastMessageElem) {
				lastMessageElem.scrollIntoView({ block: "nearest", inline: "start" });
			}
		}
	} catch (error) {
		//console.error(`[utils.ts] Error scrolling to ${direction}:`, error);
	}
}

/**
 * Specialized method for scrolling to the top of the chat (first message)
 * Handles different browser behaviors, especially for iOS/Mac devices
 * @param options Object containing all scroll parameters
 */
export function scrollChatToTop(options: ScrollOptions): void {
	scrollWithPreference(options, "top");
}

/**
 * Specialized method for scrolling to the bottom of the chat (input area)
 * Handles different browser behaviors, especially for iOS/Mac devices
 * @param options Object containing all scroll parameters
 */
export function scrollChatToBottom(options: ScrollOptions): void {
	scrollWithPreference(options, "bottom");
}

//#endregion

//#region Device Detection

/**
 * Detect if the system is Mac Computers or iOS Devices
 * like iMac, Macbook, iPhone, iPod, or iPad.
 * @returns {boolean} true if the system is Mac Computers or iOS Devices, false otherwise.
 */
function isMacLike(): boolean {
	try {
		const userAgent = navigator.userAgent || "unknown";
		const appleDeviceRegex = /Mac|iPhone|iPod|iPad/i;
		return appleDeviceRegex.test(userAgent);
	} catch (ex) {
		return false;
	}
}

//#endregion

//#region DOM Selector Utilities

/**
 * Gets interactive elements in the chat (buttons, links, inputs, etc.)
 *
 * @param prefixClassName The CSS class prefix used in the application
 * @param visibleOnly Whether to only return visible elements
 * @returns jQuery object containing the interactive elements
 */
export function getInteractiveElements(prefixClassName: string, visibleOnly: boolean = false): JQuery<HTMLElement> {
	const visibilityFilter = visibleOnly ? ":visible" : "";

	return jQuery(
		`.${prefixClassName}chatBody main button${visibilityFilter}, ` +
			`.${prefixClassName}chatBody main a[href]${visibilityFilter}, ` +
			`.${prefixClassName}chatBody main [tabindex]:not([tabindex="-1"])${visibilityFilter}, ` +
			`.${prefixClassName}chatBody main input:not([disabled])${visibilityFilter}, ` +
			`.${prefixClassName}chatBody main select:not([disabled])${visibilityFilter}, ` +
			`.${prefixClassName}chatBody main textarea:not([disabled])${visibilityFilter}`
	);
}

//#endregion

//#region Keyboard Event Helpers

/**
 * Checks if a keyboard event is a Ctrl+Key combination
 * cross-platform (Ctrl on Windows, Cmd on Mac).
 *
 * @param event - The KeyboardEvent to check
 * @param key - The key to check for (e.g., "ArrowUp", "ArrowDown")
 * @returns True if the event represents a Ctrl+specified key combination
 */
export function isCtrlKeyPressed(event: KeyboardEvent, key: string): boolean {
	try {
		if (!event || !key) return false;

		// Determine which modifier key to check based on the OS.
		// On Mac, check for the Command key (metaKey).
		// On other systems (Windows, Linux), check for the Control key (ctrlKey).
		const isModifierPressed = isMacLike() ? event.metaKey : event.ctrlKey;
		const normalizedKey = StringUtils.normalizeString(key);
		const normalizedEventKey = StringUtils.normalizeString(event.key);

		// Check if the modifier is pressed AND the specified key matches.
		return isModifierPressed && normalizedEventKey === normalizedKey;
	} catch (ex) {
		return false;
	}
}

/**
 * Determines if a keyboard event is a Ctrl+Up Arrow key combination.
 *
 * @param event - The keyboard event to check
 * @returns `true` if the event represents a Ctrl+Up Arrow key combination, `false` otherwise
 */
export function isCtrlUpArrow(event: KeyboardEvent): boolean {
	return isCtrlKeyPressed(event, "ArrowUp");
}

/**
 * Checks if a keyboard event is a Ctrl+Down Arrow key combination.
 *
 * @param event - The KeyboardEvent to check
 * @returns True if the event represents a Ctrl+Down Arrow key combination, false otherwise
 */
export function isCtrlDownArrow(event: KeyboardEvent): boolean {
	return isCtrlKeyPressed(event, "ArrowDown");
}

//#endregion
